import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { produce } from 'immer';
import { retry } from 'rxjs';
import { NgxsStateFacade } from '../../shared/store/ngxs-state-facade';
import { ProfileState } from '../../profile/store/profile.state';
import { NavigateService } from '../../shared/services/navigate.service';
import { TvhPaymentStatus } from '../constants/payment-status';
import { TvhPayment } from '../models/payment';
import { PaymentService } from '../services/payment.service';

export interface PaymentLandingStateModel {
  paymentId: number;
  pending: boolean;
  retrievalFailed: boolean;
}

export class SetPaymentId {
  static readonly type = '[PaymentLanding] Set Payment Id';
  constructor(public paymentId: number) {}
}

export class MarkResolved {
  static readonly type = '[PaymentLanding] Mark Resolved';
}

export class MarkRetrievalFailed {
  static readonly type = '[PaymentLanding] Mark Retrieval Failed';
}

@State<PaymentLandingStateModel>({
  name: 'paymentLanding',
  defaults: {
    paymentId: 0,
    pending: true,
    retrievalFailed: false,
  },
})
@Injectable()
export class PaymentLandingState extends NgxsStateFacade<PaymentLandingStateModel> {
  constructor(
    store: Store,
    private readonly navigate: NavigateService,
    private readonly paymentSvc: PaymentService,
    private readonly profileState: ProfileState,
  ) {
    super(store, 'paymentLanding');
  }

  @Selector()
  static isPending(state: PaymentLandingStateModel): boolean {
    return state.pending;
  }

  @Selector()
  static hasRetrievalFailed(state: PaymentLandingStateModel): boolean {
    return state.retrievalFailed;
  }

  // --- Public API ---

  get isPending(): boolean {
    return this.store.selectSnapshot(PaymentLandingState.isPending);
  }

  get hasRetrievalFailed(): boolean {
    return this.store.selectSnapshot(PaymentLandingState.hasRetrievalFailed);
  }

  setPaymentId(paymentId: number): void {
    this.store.dispatch(new SetPaymentId(paymentId));
  }

  markResolved(): void {
    this.store.dispatch(new MarkResolved());
  }

  markRetrievalFailed(): void {
    this.store.dispatch(new MarkRetrievalFailed());
  }

  // --- Actions ---

  @Action(SetPaymentId)
  setPaymentIdHandler(ctx: StateContext<PaymentLandingStateModel>, action: SetPaymentId) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.paymentId = action.paymentId;
        draft.pending = true;
        draft.retrievalFailed = false;
      }),
    );
    this.checkPaymentStatus(action.paymentId);
  }

  @Action(MarkResolved)
  markResolvedHandler(ctx: StateContext<PaymentLandingStateModel>) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.pending = false;
      }),
    );
  }

  @Action(MarkRetrievalFailed)
  markRetrievalFailedHandler(ctx: StateContext<PaymentLandingStateModel>) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.pending = false;
        draft.retrievalFailed = true;
      }),
    );
  }

  private checkPaymentStatus(paymentId: number): void {
    this.paymentSvc
      .getPayment(paymentId, true)
      .pipe(retry(1))
      .subscribe({
        next: (p: TvhPayment) => {
          switch (p.status) {
            case TvhPaymentStatus.CANCELED:
            case TvhPaymentStatus.EXPIRED:
            case TvhPaymentStatus.FAILED:
              this.paymentError(paymentId);
              break;
            case TvhPaymentStatus.PAID:
            case TvhPaymentStatus.CREATED:
              this.paymentSuccess(paymentId);
              break;
            default:
              setTimeout(() => {
                this.checkPaymentStatus(paymentId);
              }, 1000);
          }
        },
        error: () => {
          this.markRetrievalFailed();
        },
      });
  }

  private paymentError(paymentId: number): void {
    this.markResolved();
    this.navigate.toPaymentError(paymentId);
  }

  private paymentSuccess(paymentId: number): void {
    this.profileState.refreshBalance();
    this.markResolved();
    this.navigate.toPaymentSuccess(paymentId);
  }
}
